import { Head, Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '../../layouts/Layout'

interface User {
  id: string
  username: string
  fullName: string
  admin: boolean
}

interface Follower {
  id: string
  username: string
}

interface Post {
  id: string
  imageUrl: string
  username: string
  createdAt: string
}

interface Props {
  currentUser?: User | null
  followers?: Follower[]
  following?: Follower[]
  feed?: Post[]
  allUsers?: Array<{ username: string; postsCount: number; timeZone: string }>
  qrCodeSvg?: string
}

export default function WelcomeIndex({ currentUser, followers, following, feed, allUsers, qrCodeSvg }: Props) {
  // Admin view - shows all data
  if (currentUser?.username === 'alexbaldwin') {
    return (
      <Layout title="Home">
        <div className="container mx-auto px-4 py-8">
          <h3 className="text-lg font-semibold mb-2">Followers</h3>
          <ul className="mb-6">
            {followers?.map((user) => (
              <li key={user.id}>
                <Link href={`/${user.username}`} className="text-blue-600 hover:underline">
                  {user.username}
                </Link>
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-semibold mb-2">Following</h3>
          <ul className="mb-6">
            {following?.map((user) => (
              <li key={user.id}>
                <Link href={`/${user.username}`} className="text-blue-600 hover:underline">
                  {user.username}
                </Link>
                <span className="text-gray-500 ml-2">{user.id}</span>
              </li>
            ))}
          </ul>

          <h3 className="text-lg font-semibold mb-2">Posts (last 30 days)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {feed?.map((post) => (
              <div key={post.id} className="w-48">
                <img src={post.imageUrl} alt="" className="w-full block" />
                <p className="text-sm text-gray-600 mt-1">
                  {post.username} - {post.createdAt}
                </p>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold mb-2">All Users</h3>
          <ul>
            {allUsers?.map((user) => (
              <li key={user.username}>
                <Link href={`/${user.username}`} className="text-blue-600 hover:underline">
                  {user.username} ({user.postsCount})
                </Link>
                <span className="text-gray-500 ml-2">{user.timeZone}</span>
              </li>
            ))}
          </ul>
        </div>
      </Layout>
    )
  }

  // Logged in user (non-admin) - no feed message
  if (currentUser) {
    return (
      <Layout title="Home">
        <div className="container mx-auto px-4 py-8">
          <p className="text-gray-600">
            Sorry, there's no feed. You'll need to find friends the old fashioned way and text them
            for their username.
          </p>
        </div>
      </Layout>
    )
  }

  // Public landing page
  return (
    <>
      <Head title="three.pm - Share one real moment every day" />
      <div className="min-h-screen flex items-center justify-center relative">
        {/* QR Code - Top Right */}
        <div className="fixed top-4 right-4">
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-lg">
            <div className="mb-2">
              {qrCodeSvg ? (
                <div
                  className="w-24 h-24"
                  dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
                />
              ) : (
                <div className="w-24 h-24 bg-gray-100 rounded flex items-center justify-center">
                  <span className="text-xs text-gray-400">QR Code</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 text-center">Scan with your phone</p>
          </div>
        </div>

        {/* Sign In Link - Top Left */}
        <div className="fixed top-4 left-4">
          <Link
            href="/users/sign_in"
            className="text-sm text-gray-500 hover:text-black transition-colors"
          >
            Sign in
          </Link>
        </div>

        {/* Main Content */}
        <div className="text-center px-4 max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-normal mb-6 leading-tight">
            At three.pm every day, share one real moment with the people who matter most.
          </h1>

          <a
            href="sms://imessage@three.pm"
            className="inline-block bg-black text-white py-3 px-6 rounded-full hover:bg-gray-800 transition-colors"
          >
            Message us to join
          </a>
          <p className="text-sm text-gray-500 mt-3">Use iMessage to start your account</p>
        </div>
      </div>
    </>
  )
}

// No layout for landing page - clean full-screen experience
WelcomeIndex.layout = (page: ReactNode) => page
